/**
 * ScopePickerModal.tsx
 *
 * Modal dialog to select applicable colors and applicable assembly for a Master line.
 * Spec: docs/superpowers/plans/2026-09-22-bom-master-composer-spec.md
 */
import { useEffect, useState } from 'react'
import { X, Check } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import type { ApplicableAssemblyOption } from '@/services/boms/masterTypes'

interface ScopePickerModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (
    colors: string[],
    assembly: ApplicableAssemblyOption[],
  ) => Promise<void>
  initialColors: string[]
  initialAssembly: ApplicableAssemblyOption[]
  availableColors: Array<{ code: string; name: string }>
  lineName: string
  lineType: 'wood' | 'hardware'
}

export function ScopePickerModal({
  isOpen,
  onClose,
  onSave,
  initialColors,
  initialAssembly,
  availableColors,
  lineName,
  lineType,
}: ScopePickerModalProps) {
  const [selectedColors, setSelectedColors] = useState<string[]>(['ALL'])
  const [selectedAssembly, setSelectedAssembly] = useState<
    ApplicableAssemblyOption[]
  >(['ALL'])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      setSelectedColors(
        initialColors && initialColors.length > 0 ? initialColors : ['ALL'],
      )
      setSelectedAssembly(
        initialAssembly && initialAssembly.length > 0
          ? initialAssembly
          : ['ALL'],
      )
      setError(null)
    }
  }, [isOpen, initialColors, initialAssembly])

  if (!isOpen) return null

  // Color selection logic:
  // If 'ALL' is checked, clear specific colors.
  // If specific color checked, remove 'ALL'.
  const handleToggleColor = (code: string) => {
    if (code === 'ALL') {
      setSelectedColors(['ALL'])
      return
    }

    let next = selectedColors.filter((c) => c !== 'ALL')
    if (next.includes(code)) {
      next = next.filter((c) => c !== code)
    } else {
      next.push(code)
    }

    // If no colors selected, default back to 'ALL'
    if (next.length === 0) {
      next = ['ALL']
    }
    setSelectedColors(next)
  }

  // Assembly selection logic:
  // If 'ALL' is checked, clear specific.
  const handleToggleAssembly = (opt: ApplicableAssemblyOption) => {
    if (opt === 'ALL') {
      setSelectedAssembly(['ALL'])
      return
    }

    let next: ApplicableAssemblyOption[] = selectedAssembly.filter((a) => a !== 'ALL')
    if (next.includes(opt)) {
      next = next.filter((a) => a !== opt)
    } else {
      next.push(opt)
    }

    if (next.length === 0) {
      next = ['ALL']
    }
    setSelectedAssembly(next)
  }

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      await onSave(selectedColors, selectedAssembly)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Lưu phạm vi thất bại')
    } finally {
      setSaving(false)
    }
  }

  const isColorAll = selectedColors.includes('ALL')
  const isAssemblyAll = selectedAssembly.includes('ALL')

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-lg flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h3 className="text-base font-bold text-slate-900">
              Phạm vi áp dụng chi tiết
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {lineType === 'wood' ? 'Chi tiết gỗ' : 'Ngũ kim'}:{' '}
              <span className="font-semibold text-slate-800">{lineName}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {error && (
            <div className="p-3 text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded-lg">
              {error}
            </div>
          )}

          {/* 1. Màu áp dụng */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
              1. Màu sắc áp dụng
            </label>
            <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
              <label
                className={`flex items-center gap-2.5 p-2 rounded-lg border text-xs cursor-pointer transition-colors ${
                  isColorAll
                    ? 'bg-indigo-50 border-indigo-300 text-indigo-900 font-semibold'
                    : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100'
                }`}
              >
                <input
                  type="checkbox"
                  checked={isColorAll}
                  onChange={() => handleToggleColor('ALL')}
                  className="rounded text-indigo-600 focus:ring-indigo-500"
                />
                <span>TẤT CẢ CÁC MÀU (Dùng chung)</span>
              </label>

              <div className="grid grid-cols-2 gap-2 mt-2">
                {availableColors.map((col) => {
                  const checked =
                    !isColorAll && selectedColors.includes(col.code)
                  return (
                    <label
                      key={col.code}
                      className={`flex items-center gap-2 p-2 rounded-lg border text-xs cursor-pointer transition-colors ${
                        checked
                          ? 'bg-indigo-50 border-indigo-300 text-indigo-900 font-medium'
                          : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => handleToggleColor(col.code)}
                        className="rounded text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="truncate">
                        <strong className="font-mono text-indigo-700">
                          {col.code}
                        </strong>{' '}
                        — {col.name}
                      </span>
                    </label>
                  )
                })}
              </div>
            </div>
          </div>

          {/* 2. Kết cấu áp dụng */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
              2. Kết cấu lắp ráp áp dụng
            </label>
            <div className="space-y-2">
              <label
                className={`flex items-center gap-2.5 p-2 rounded-lg border text-xs cursor-pointer transition-colors ${
                  isAssemblyAll
                    ? 'bg-indigo-50 border-indigo-300 text-indigo-900 font-semibold'
                    : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100'
                }`}
              >
                <input
                  type="checkbox"
                  checked={isAssemblyAll}
                  onChange={() => handleToggleAssembly('ALL')}
                  className="rounded text-indigo-600 focus:ring-indigo-500"
                />
                <span>TẤT CẢ KẾT CẤU (Cả Ráp chết & Tháo rời)</span>
              </label>

              <div className="grid grid-cols-2 gap-2 mt-2">
                <label
                  className={`flex items-center gap-2 p-2 rounded-lg border text-xs cursor-pointer transition-colors ${
                    !isAssemblyAll && selectedAssembly.includes('assembled')
                      ? 'bg-indigo-50 border-indigo-300 text-indigo-900 font-medium'
                      : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={
                      !isAssemblyAll && selectedAssembly.includes('assembled')
                    }
                    onChange={() => handleToggleAssembly('assembled')}
                    className="rounded text-indigo-600 focus:ring-indigo-500"
                  />
                  <span>Ráp chết (assembled / SX)</span>
                </label>

                <label
                  className={`flex items-center gap-2 p-2 rounded-lg border text-xs cursor-pointer transition-colors ${
                    !isAssemblyAll && selectedAssembly.includes('kd')
                      ? 'bg-indigo-50 border-indigo-300 text-indigo-900 font-medium'
                      : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={!isAssemblyAll && selectedAssembly.includes('kd')}
                    onChange={() => handleToggleAssembly('kd')}
                    className="rounded text-indigo-600 focus:ring-indigo-500"
                  />
                  <span>Tháo rời (kd / KD)</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-slate-200 bg-slate-50">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Hủy
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700"
          >
            <Check className="h-4 w-4" />
            <span>{saving ? 'Đang lưu...' : 'Lưu phạm vi'}</span>
          </Button>
        </div>
      </div>
    </div>
  )
}
